import React, {
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";
import { GoogleCalendarConnection, GoogleCalendarInfo } from "../types";
import { GoogleCalendarContext } from "./googleCalendarContextDef";
import toast from "react-hot-toast";

interface GoogleCalendarProviderProps {
  children: ReactNode;
}

export const GoogleCalendarProvider: React.FC<GoogleCalendarProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [connection, setConnection] = useState<GoogleCalendarConnection | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchConnection = useCallback(async () => {
    if (!user) {
      setConnection(null);
      setIsLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("google_calendar_connections")
      .select("id, user_id, sync_enabled, hidden_calendar_ids, google_calendar_id, connected_at, last_synced_at, week_start_day")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error || !data) {
      setConnection(null);
    } else {
      setConnection(data);
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchConnection();
  }, [fetchConnection]);

  // One-time migration: fetch week_start_day for existing connections that don't have it
  useEffect(() => {
    if (!connection || connection.week_start_day !== null) return;

    let cancelled = false;
    (async () => {
      await supabase.functions.invoke("google-calendar-auth", {
        body: { action: "fetch-settings" },
      });
      if (!cancelled) fetchConnection();
    })();
    return () => { cancelled = true; };
  }, [connection, fetchConnection]);

  // Listen for postMessage from OAuth popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "google-calendar-connected") {
        fetchConnection();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [fetchConnection]);

  const connectGoogleCalendar = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke("google-calendar-auth", {
      body: { action: "get-auth-url" },
    });

    if (error || !data?.url) {
      console.error("Failed to get auth URL:", error);
      return;
    }

    // Open OAuth flow in a popup
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    window.open(
      data.url,
      "google-calendar-auth",
      `width=${width},height=${height},left=${left},top=${top}`,
    );
  }, []);

  const disconnectGoogleCalendar = useCallback(async () => {
    const { error } = await supabase.functions.invoke("google-calendar-auth", {
      body: { action: "disconnect" },
    });

    if (error) {
      console.error("Failed to disconnect:", error);
      toast.error("Couldn't disconnect Google Calendar. Please try again.");
      return;
    }

    setConnection(null);
  }, []);

  const toggleSync = useCallback(async () => {
    if (!connection) return;

    const newSyncEnabled = !connection.sync_enabled;
    const { error } = await supabase
      .from("google_calendar_connections")
      .update({ sync_enabled: newSyncEnabled })
      .eq("id", connection.id);

    if (error) {
      console.error("Failed to toggle sync:", error);
      return;
    }

    setConnection({ ...connection, sync_enabled: newSyncEnabled });
  }, [connection]);

  const fetchCalendarList = useCallback(async (): Promise<GoogleCalendarInfo[]> => {
    const { data, error } = await supabase.functions.invoke("google-calendar-auth", {
      body: { action: "list-calendars" },
    });

    if (error || !data?.calendars) {
      console.error("Failed to fetch calendar list:", error);
      return [];
    }

    return data.calendars;
  }, []);

  const toggleCalendarVisibility = useCallback(async (calendarId: string) => {
    if (!connection) return;

    const isHidden = connection.hidden_calendar_ids.includes(calendarId);
    const newHiddenIds = isHidden
      ? connection.hidden_calendar_ids.filter((id) => id !== calendarId)
      : [...connection.hidden_calendar_ids, calendarId];

    const { error } = await supabase
      .from("google_calendar_connections")
      .update({ hidden_calendar_ids: newHiddenIds })
      .eq("id", connection.id);

    if (error) {
      console.error("Failed to update calendar visibility:", error);
      return;
    }

    setConnection({ ...connection, hidden_calendar_ids: newHiddenIds });
  }, [connection]);

  const setHiddenCalendarIds = useCallback(async (calendarIds: string[]) => {
    if (!connection) return;

    const { error } = await supabase
      .from("google_calendar_connections")
      .update({ hidden_calendar_ids: calendarIds })
      .eq("id", connection.id);

    if (error) {
      toast.error("Failed to update calendar visibility");
      return;
    }

    setConnection({ ...connection, hidden_calendar_ids: calendarIds });
  }, [connection]);

  return (
    <GoogleCalendarContext.Provider
      value={{
        connection,
        isConnected: !!connection,
        isLoading,
        connectGoogleCalendar,
        disconnectGoogleCalendar,
        toggleSync,
        fetchCalendarList,
        toggleCalendarVisibility,
        setHiddenCalendarIds,
      }}
    >
      {children}
    </GoogleCalendarContext.Provider>
  );
};
